import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'

import { useBookingContext } from '@providers/booking'
import { TGroomResource } from '@providers/booking/types'
import { hasRequestDates } from 'components-ui/src/shared/location'
import { i18WithParams as t } from '@shared/locale'
import { Spacer } from 'components-ui/src/spacer/spacer'
import { ANY_GROOMER_ID, anyGroomer, getAvailableProviders, getSortedGroomers } from './helper'
import { getInStayLBC } from './request_dates/helper'
import type { TPetOffers } from './types'

export const GroomerSelector = ({ groomOffer }: { groomOffer: TPetOffers }) => {
  const bookingCtx = useBookingContext()
  const theme = useTheme()
  const isBelowMD = useMediaQuery(theme.breakpoints.down('md'))
  const groomers = getAvailableProviders(groomOffer)
  const baseGroomers = getSortedGroomers(groomers)
  const sortedGroomers = [anyGroomer, ...baseGroomers]

  const onRemoveGroomHandler = () => {
    bookingCtx.dispatch({
      type: 'REMOVE_GROOM_FOR_PET',
      payload: {
        groupIdx: groomOffer.groupIdx,
        petIdx: groomOffer.pet.petIdx,
      },
    })
  }

  const onSelectGroomerHandler = (groomer: TGroomResource) => {
    bookingCtx.dispatch({
      type: 'SELECT_GROOMER',
      payload: {
        groupIdx: groomOffer.groupIdx,
        petIdx: groomOffer.pet.petIdx,
        groomer,
        anyGroomer: groomer.id == ANY_GROOMER_ID,
      },
    })

    const mustRequestDates = hasRequestDates(getInStayLBC(bookingCtx.state, 'grooming'))

    bookingCtx.dispatch({
      type: 'SET_GROOM_WIZARD_STATE',
      payload: {
        groupIdx: groomOffer.groupIdx,
        petIdx: groomOffer.pet.petIdx,
        state: mustRequestDates ? 'dateSelector' : 'groomer',
        isDone: !mustRequestDates,
      },
    })
  }

  return (
    <>
      <Paper elevation={1} sx={{ p: 2, borderRadius: `${isBelowMD ? 0 : theme.shape.borderRadius}px` }}>
        <Typography variant="h5">{t('groomingWizard.selectGroomer', { petName: groomOffer.pet.name })} </Typography>
        <Spacer height={3} />
        {baseGroomers && baseGroomers.length > 0 ? (
          sortedGroomers?.map((groomer) => {
            const isSelected = groomOffer.resource?.id === groomer.id
            return (
              <Stack
                key={groomer.id}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
                sx={{ py: 2, borderBottom: '1px solid', borderColor: theme.palette.divider }}
              >
                <Typography variant="body1">{groomer.displayName}</Typography>
                {groomer.available ? (
                  <Button
                    variant={isSelected ? 'contained' : 'outlined'}
                    onClick={() => onSelectGroomerHandler(groomer)}
                  >
                    {isSelected ? t('global.label.selected') : t('global.label.select')}
                  </Button>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    {t('groomingWizard.groomerUnavailable')}
                  </Typography>
                )}
              </Stack>
            )
          })
        ) : (
          <Typography variant="body1" color="textSecondary">
            {t('groomingWizard.noGroomersAvailable')}
          </Typography>
        )}
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Button variant="text" onClick={onRemoveGroomHandler}>
            {t('groomingWizard.remove')}
          </Button>
        </Box>
      </Paper>
    </>
  )
}
